import { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';

export const Datenschutz = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <SEO page="datenschutz" />
      <Header />
      
      <main className="flex-1 pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#E31E24] hover:text-[#C41217] mb-8 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </Link>

          <h1 className="text-4xl font-bold text-neutral-900 mb-4">Datenschutzerklärung</h1>
          <p className="text-xl text-neutral-600 mb-8">FESCH Elektrotechnik GmbH</p>
          
          <div className="prose prose-lg max-w-none text-neutral-700">
            
            {/* 1. Verantwortlicher */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">1. Verantwortlicher</h2>
              <p>
                <strong>FESCH Elektrotechnik GmbH</strong><br />
                An der Bergbahn 2a<br />
                42289 Wuppertal<br />
                Deutschland
              </p>
              <p className="mt-4">
                Telefon: 0202 / 430 445 10<br />
                E-Mail: info@fesch-elektrotechnik.de
              </p>
              <p className="mt-4">
                Der Verantwortliche entscheidet über die Zwecke und Mittel der Verarbeitung personenbezogener Daten.
              </p>
            </section>

            {/* 2. Allgemeine Hinweise zur Datenverarbeitung */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">2. Datensparsamkeit und Privacy by Design</h2>
              <p>
                Unsere Website wurde nach dem Prinzip der <strong>Datensparsamkeit</strong> und <strong>Privacy by Design</strong> entwickelt. Wir verzichten bewusst auf Drittanbieter-Dienste, die ohne technische Notwendigkeit personenbezogene Daten erheben.
              </p>
              <p className="mt-4">Das bedeutet konkret:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Es werden <strong>keine Tracking-, Analyse- oder Marketing-Cookies</strong> gesetzt (kein Google Analytics, kein Tag Manager, keine Pixel).</li>
                <li>Es werden <strong>keine externen Schriftarten</strong> geladen – alle Fonts (Inter, Barlow Condensed) liegen lokal auf unserem Hosting-Server und werden von dort ausgeliefert.</li>
                <li>Es werden <strong>keine externen Bild-CDNs</strong> verwendet – sämtliche Bilder, Logos und Grafiken werden lokal vom Hosting-Server ausgeliefert.</li>
                <li>Es werden <strong>keine Social-Media-Plug-ins</strong> mit automatischem Datenabruf eingesetzt. Links zu sozialen Netzwerken sind reine HTML-Verweise; ein Aufruf erfolgt erst nach aktivem Klick durch Sie.</li>
                <li>Es wird ein einziger <strong>technisch notwendiger localStorage-Eintrag</strong> (<code>fesch_consent_v1</code>) gesetzt, um Ihre Cookie-Einwilligung rechtskonform zu dokumentieren (siehe Ziffer 8).</li>
              </ul>
              <p className="mt-4">
                Eine Einbindung von Fremdinhalten erfolgt ausschließlich an den in dieser Erklärung genannten Stellen und ausschließlich auf Basis der dort beschriebenen Rechtsgrundlagen.
              </p>
            </section>

            {/* 2b. Allgemeine Hinweise */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">3. Allgemeine Hinweise zur Datenverarbeitung</h2>
              <p>
                Wir verarbeiten personenbezogene Daten ausschließlich im Einklang mit den geltenden datenschutzrechtlichen Vorschriften, insbesondere der Datenschutz-Grundverordnung (DSGVO).
              </p>
              <p className="mt-4">
                Personenbezogene Daten sind alle Informationen, die sich auf eine identifizierte oder identifizierbare Person beziehen (z. B. Name, E-Mail-Adresse, Telefonnummer oder IP-Adresse).
              </p>
              <p className="mt-4">
                Die Verarbeitung erfolgt nur, wenn eine rechtliche Grundlage besteht, insbesondere:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</li>
                <li>Art. 6 Abs. 1 lit. b DSGVO (Vertrag / Anfrage)</li>
                <li>Art. 6 Abs. 1 lit. c DSGVO (gesetzliche Pflicht)</li>
                <li>Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)</li>
              </ul>
            </section>

            {/* 3. Zugriffsdaten und Server-Logfiles */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">4. Zugriffsdaten und Server-Logfiles</h2>
              <p>
                Beim Aufruf unserer Website werden automatisch Informationen erfasst und in Server-Logfiles gespeichert.
              </p>
              <p className="mt-4">Dies umfasst insbesondere:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>IP-Adresse des anfragenden Geräts</li>
                <li>Datum und Uhrzeit des Zugriffs</li>
                <li>Browsertyp und Browserversion</li>
                <li>Betriebssystem</li>
                <li>Referrer-URL</li>
                <li>aufgerufene Seiten / Dateien</li>
              </ul>
              <p className="mt-4">Diese Daten sind technisch erforderlich, um:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>die Website korrekt auszuliefern</li>
                <li>die Stabilität und Sicherheit zu gewährleisten</li>
                <li>Missbrauch und Angriffe zu erkennen</li>
              </ul>
              <p className="mt-4">
                Eine Zusammenführung dieser Daten mit anderen Datenquellen erfolgt nicht.
              </p>
              <p className="mt-4">
                <strong>Rechtsgrundlage:</strong><br />
                Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an sicherem Betrieb)
              </p>
            </section>

            {/* 4. Hosting und Bereitstellung der Website (Vercel) */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">5. Hosting und Bereitstellung der Website (Vercel)</h2>
              <p>
                Unsere Website wird über die Plattform Vercel Inc. bereitgestellt.
              </p>
              <p className="mt-4">Im Rahmen des Hostings werden folgende Daten verarbeitet:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>IP-Adressen</li>
                <li>Verbindungsdaten</li>
                <li>technische Nutzungsdaten</li>
                <li>Server-Logfiles</li>
              </ul>
              <p className="mt-4">Diese Verarbeitung ist erforderlich, um:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>die Website bereitzustellen</li>
                <li>Inhalte auszuliefern</li>
                <li>die Performance zu optimieren</li>
                <li>die Sicherheit zu gewährleisten</li>
              </ul>
              <p className="mt-4">
                Es kann dabei zu einer Verarbeitung von Daten in Drittstaaten (z. B. USA) kommen. In solchen Fällen erfolgt die Verarbeitung auf Grundlage geeigneter Garantien (z. B. Standardvertragsklauseln).
              </p>
              <p className="mt-4">
                <strong>Rechtsgrundlage:</strong><br />
                Art. 6 Abs. 1 lit. f DSGVO
              </p>
              <p className="mt-4">
                Mit Vercel besteht ein Vertrag zur Auftragsverarbeitung (AVV).
              </p>
            </section>

            {/* 5. Domainverwaltung und technische Infrastruktur (IONOS) */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">6. Domainverwaltung und technische Infrastruktur (IONOS)</h2>
              <p>
                Unsere Domain wird über IONOS SE verwaltet.
              </p>
              <p className="mt-4">IONOS verarbeitet im Rahmen der technischen Bereitstellung insbesondere:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>IP-Adressen</li>
                <li>DNS-Anfragen</li>
                <li>Verbindungsdaten</li>
              </ul>
              <p className="mt-4">Diese Verarbeitung ist notwendig für:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Erreichbarkeit der Website</li>
                <li>stabile Verbindung</li>
                <li>technische Infrastruktur</li>
              </ul>
              <p className="mt-4">
                <strong>Rechtsgrundlage:</strong><br />
                Art. 6 Abs. 1 lit. f DSGVO
              </p>
              <p className="mt-4">
                Mit IONOS besteht ein Vertrag zur Auftragsverarbeitung (AVV).
              </p>
            </section>

            {/* 6. Kontaktformular und Kommunikation (Resend) */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">7. Kontaktformular und Kommunikation (Resend)</h2>
              <p>
                Wenn Sie uns über das Kontaktformular oder per E-Mail kontaktieren, verarbeiten wir Ihre Angaben zur Bearbeitung Ihrer Anfrage.
              </p>
              <p className="mt-4"><strong>Verarbeitete Daten:</strong></p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Name</li>
                <li>E-Mail-Adresse</li>
                <li>Telefonnummer (optional)</li>
                <li>Nachricht / Anfrage</li>
              </ul>
              <p className="mt-4">Die Datenverarbeitung erfolgt zur:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Bearbeitung Ihrer Anfrage</li>
                <li>Kontaktaufnahme</li>
                <li>Durchführung vorvertraglicher Maßnahmen</li>
              </ul>
              <p className="mt-4">
                Für den technischen Versand der Nachrichten nutzen wir den Dienst Resend, Inc.
              </p>
              <p className="mt-4">
                Dabei werden die eingegebenen Daten sowie technische Metadaten (z. B. Versandinformationen) an Resend übermittelt.
              </p>
              <p className="mt-4"><strong>Rechtsgrundlagen:</strong></p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Art. 6 Abs. 1 lit. b DSGVO (Anfrage / Vertrag)</li>
                <li>Art. 6 Abs. 1 lit. f DSGVO (effiziente Kommunikation)</li>
                <li>ggf. Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</li>
              </ul>
              <p className="mt-4">
                Mit Resend besteht ein Vertrag zur Auftragsverarbeitung (AVV).
              </p>
            </section>

            {/* 8. Cookies und Einwilligungsmanagement */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">8. Cookies und Einwilligungsmanagement</h2>
              <p>
                Unsere Website setzt aktuell <strong>keine Tracking-, Analyse- oder Marketing-Cookies</strong>.
                Es werden weder Google Analytics noch Tag Manager, Pixel oder vergleichbare Technologien eingebunden.
              </p>

              <h3 className="text-xl font-bold text-neutral-900 mt-6 mb-3">8.1 Technisch notwendige Speicherung (Einwilligungsmanagement)</h3>
              <p>
                Damit wir Ihre Auswahl im Cookie-/Consent-Banner speichern und respektieren können, wird
                ein einziger lokaler Speichereintrag (<code>fesch_consent_v1</code>) im
                <em> localStorage</em> Ihres Browsers abgelegt. Dieser enthält ausschließlich:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Zeitpunkt Ihrer Entscheidung</li>
                <li>ausgewählte Kategorien (Essenziell / Analyse / Marketing)</li>
              </ul>
              <p className="mt-4">
                Es handelt sich um einen technisch notwendigen Eintrag zur Erfüllung der gesetzlichen
                Dokumentationspflicht für Einwilligungen. Es werden dabei keine Daten an Dritte übermittelt.
              </p>
              <p className="mt-4">
                <strong>Rechtsgrundlage:</strong><br />
                § 25 Abs. 2 Nr. 2 TDDDG, Art. 6 Abs. 1 lit. f DSGVO
              </p>

              <h3 className="text-xl font-bold text-neutral-900 mt-6 mb-3">8.2 Einwilligungsbedürftige Technologien (zukünftig)</h3>
              <p>
                Sollten wir künftig Analyse- oder Marketing-Dienste einsetzen (z. B. anonymisierte
                Reichweitenmessung), werden diese <strong>ausschließlich nach Ihrer vorherigen aktiven
                Einwilligung</strong> geladen. Solange keine Einwilligung vorliegt, werden keine
                entsprechenden Skripte, Cookies oder Tracker aktiv.
              </p>
              <p className="mt-4">
                Sie können Ihre Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen.
                Nutzen Sie dazu den Link <strong>„Cookie-Einstellungen"</strong> im Footer.
                Bereits gespeicherte Zustimmungs-Einträge werden dabei aktualisiert.
              </p>
              <p className="mt-4">
                <strong>Rechtsgrundlage:</strong><br />
                § 25 Abs. 1 TDDDG, Art. 6 Abs. 1 lit. a DSGVO
              </p>
            </section>

            {/* 8. Speicherdauer */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">9. Speicherdauer</h2>
              <p>
                Wir speichern personenbezogene Daten nur so lange, wie es für den jeweiligen Zweck erforderlich ist.
              </p>
              <ul className="list-disc list-inside mt-4 space-y-1">
                <li>Kontaktanfragen: bis zur abschließenden Bearbeitung</li>
                <li>gesetzliche Aufbewahrungspflichten (z. B. steuerrechtlich) bleiben unberührt</li>
              </ul>
              <p className="mt-4">
                Nach Wegfall des Zwecks werden die Daten gelöscht.
              </p>
            </section>

            {/* 9. Weitergabe von Daten */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">10. Weitergabe von Daten</h2>
              <p>
                Eine Weitergabe personenbezogener Daten erfolgt nur:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>an Auftragsverarbeiter (z. B. Vercel, Resend, IONOS)</li>
                <li>wenn dies zur Vertragserfüllung erforderlich ist</li>
                <li>wenn eine gesetzliche Verpflichtung besteht</li>
                <li>oder wenn Sie eingewilligt haben</li>
              </ul>
              <p className="mt-4">
                Alle eingesetzten Dienstleister sind vertraglich zur Einhaltung der Datenschutzvorschriften verpflichtet.
              </p>
            </section>

            {/* 10. Datenübermittlung in Drittländer */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">11. Datenübermittlung in Drittländer</h2>
              <p>
                Sofern Daten an Dienstleister außerhalb der EU übermittelt werden (z. B. USA), erfolgt dies nur unter Einhaltung der gesetzlichen Voraussetzungen.
              </p>
              <p className="mt-4">Dies geschieht insbesondere auf Grundlage:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>EU-Standardvertragsklauseln (SCC)</li>
                <li>geeigneter technischer und organisatorischer Maßnahmen</li>
              </ul>
            </section>

            {/* 11. Sicherheit der Verarbeitung */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">12. Sicherheit der Verarbeitung</h2>
              <p>
                Wir setzen technische und organisatorische Maßnahmen ein, um Ihre Daten zu schützen, insbesondere:
              </p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>SSL-/TLS-Verschlüsselung</li>
                <li>Zugriffsbeschränkungen</li>
                <li>sichere Serverinfrastruktur</li>
                <li>regelmäßige Updates</li>
              </ul>
              <p className="mt-4">
                Dennoch kann eine vollständige Sicherheit bei der Datenübertragung im Internet nicht garantiert werden.
              </p>
            </section>

            {/* 12. Ihre Rechte */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">13. Ihre Rechte</h2>
              <p>Sie haben folgende Rechte:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Auskunft (Art. 15 DSGVO)</li>
                <li>Berichtigung (Art. 16 DSGVO)</li>
                <li>Löschung (Art. 17 DSGVO)</li>
                <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
                <li>Widerspruch (Art. 21 DSGVO)</li>
                <li>Widerruf von Einwilligungen (Art. 7 DSGVO)</li>
              </ul>
              <p className="mt-4">
                Zur Ausübung Ihrer Rechte genügt eine formlose Mitteilung an uns.
              </p>
            </section>

            {/* 13. Beschwerderecht bei einer Aufsichtsbehörde */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">14. Beschwerderecht bei einer Aufsichtsbehörde</h2>
              <p>
                Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren, wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer Daten gegen geltendes Recht verstößt.
              </p>
            </section>

            {/* 14. Pflicht zur Bereitstellung von Daten */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">15. Pflicht zur Bereitstellung von Daten</h2>
              <p>
                Die Bereitstellung personenbezogener Daten ist freiwillig.
                Sie ist jedoch erforderlich, um bestimmte Funktionen (z. B. Kontaktaufnahme) nutzen zu können.
              </p>
              <p className="mt-4">
                Ohne diese Daten ist eine Bearbeitung Ihrer Anfrage nicht möglich.
              </p>
            </section>

          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Datenschutz;
